import React, { useCallback, useRef, useState } from 'react';
import { Skeleton, message } from 'antd';
import styled from '@emotion/styled';
import useSWR from 'swr';
import { PER_PAGE_OTHER } from '../../../constants/common';
import { PromotionPageHeader } from '../../organisms/promotion/PromotionPageHeader';
import { PromotionTable } from '../../organisms/promotion/PromotionTable';
import { FormPromotionModal } from '../../organisms/promotion/FormPromotionModal';
import { appStyles } from '../../../styles';
import { MESSAGE_ERROR } from '../../../constants/message';
import { getTileWidgetListRepository } from '../../../repository/mytm/shop/shopRepository';
import { SHOP_TYPE } from '../../../types/shop';
import { FormScheduleTileModal } from '../../organisms/promotion/FormScheduleTileModal';
import SelectButtons from '../../atoms/select-buttons/SelectButtons';
import { useCountrySelection } from '../../../hooks/useCountrySelection';

export const Promotion = () => {
  const [pageActive, setPageActive] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenScheduleModal, setIsOpenScheduleModal] = useState(false);
  const [countrySelected, setCountrySelected] = useCountrySelection(null);
  const [scheduleRecord, setDataScheduleRecord] = useState<any>();
  const [record, setDataRecord] = useState<any>();
  const errorCount = useRef(0);

  const { data, isValidating, error, mutate } = useSWR(
    countrySelected ? [pageActive, 1, countrySelected] : null,
    (pageNumber: number) =>
      getTileWidgetListRepository({
        page: pageNumber,
        take: PER_PAGE_OTHER,
        country: countrySelected!,
        type: SHOP_TYPE.PROMOTION,
        sort: true,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const onCloseModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);

  const onCloseScheduleModal = useCallback(() => {
    setIsOpenScheduleModal(false);
  }, []);

  const handleChangePageActive = useCallback(value => {
    setPageActive(value);
  }, []);

  if (error) {
    errorCount.current += 1;
    if (errorCount.current === 1) {
      message.error(MESSAGE_ERROR.M02);
      const timeout = setTimeout(() => {
        errorCount.current = 0;
        clearTimeout(timeout);
      }, 5000);
    }
  }

  const recordPromotion = data?.widgets?.map((item: any, index: number) => ({ ...item, ...{ index } }));

  return (
    <Container>
      <PromotionPageHeader setIsOpenModal={setIsOpenModal} />
      <SelectButtons onChangeValue={(item: string) => setCountrySelected(item)} />
      {!data && (
        <Container>
          <Skeleton active />
        </Container>
      )}
      {data && (
        <PromotionTable
          onRefresh={mutate}
          records={recordPromotion || []}
          loading={loading || isValidating}
          setIsOpenModal={setIsOpenModal}
          setIsOpenScheduleModal={setIsOpenScheduleModal}
          setDataRecord={setDataRecord}
          setLoading={setLoading}
          setDataScheduleRecord={setDataScheduleRecord}
          pagination={{
            onChange: handleChangePageActive,
            pageSize: PER_PAGE_OTHER,
            current: pageActive,
            total: data?.total,
          }}
        />
      )}

      {isOpenModal && countrySelected && (
        <FormPromotionModal
          setDataRecord={setDataRecord}
          record={record}
          onRefresh={mutate}
          isOpenModal={isOpenModal}
          onClose={onCloseModal}
          countrySelected={countrySelected}
        />
      )}

      {isOpenScheduleModal && (
        <FormScheduleTileModal
          setDataRecord={setDataScheduleRecord}
          record={scheduleRecord}
          isOpenModal={isOpenScheduleModal}
          onClose={onCloseScheduleModal}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }

  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }

  & .ant-page-header {
    padding-left: 0px;
  }
`;
